import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import studentService from '../../services/api/student';
import { useQuery } from 'react-query';
// dates: display using English numerals
import { 
  CreditCard, 
  Calendar, 
  CheckCircle, 
  AlertCircle, 
  RefreshCw,
  History,
  ShieldCheck,
  ChevronLeft,
  XCircle,
  Clock,
  ExternalLink
} from 'lucide-react';
import { getImageUrl } from '../../config/apiConfig';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const StudentSubscriptions = () => {
  const navigate = useNavigate();
  const [filter, setFilter] = useState('all');

  // جلب بيانات اشتراكات الطالب من الـ API
  const { data: subscriptionsData, isLoading, error } = useQuery(
    'studentSubscriptions',
    () => studentService.getSubscriptions(),
    {
      retry: 1
    }
  );

  // منطق الفلترة البرمجي (كامل كما هو في الكود الأصلي)
  const filteredSubscriptions = subscriptionsData?.subscriptions?.map(sub => {
    // حساب الحالة الفعلية بناءً على تاريخ الانتهاء
    const endDate = new Date(sub.end_date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    endDate.setHours(0, 0, 0, 0);
    
    const actualStatus = endDate < today ? 'expired' : (sub.status === 'active' ? 'active' : sub.status);
    
    return { ...sub, status: actualStatus };
  })?.filter(sub => {
    if (filter === 'all') return true;
    if (filter === 'active') return sub.status === 'active';
    if (filter === 'expired') return sub.status === 'expired';
    if (filter === 'cancelled') return sub.status === 'cancelled';
    return true;
  }) || [];

  // دوال تنسيق الأيقونات والألوان بناءً على الحالة
  const getStatusIcon = (status) => {
    switch (status) {
      case 'active':
        return <CheckCircle size={14} />;
      case 'expired':
        return <XCircle size={14} />;
      case 'cancelled':
        return <AlertCircle size={14} />;
      default:
        return <Clock size={14} />;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-700 border-green-200';
      case 'expired':
        return 'bg-red-100 text-red-700 border-red-200';
      case 'cancelled':
        return 'bg-gray-100 text-gray-700 border-gray-200';
      default:
        return 'bg-amber-100 text-amber-700 border-amber-200';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'active':
        return 'نشط';
      case 'expired':
        return 'منتهي';
      case 'cancelled':
        return 'ملغي';
      default:
        return 'قيد المعالجة';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'غير محدد';
    try {
      return new Date(dateString).toLocaleDateString('en-GB');
    } catch (e) {
      return dateString;
    }
  };

  if (isLoading) {
    return (
      <div className="flex flex-col justify-center items-center py-24 min-h-[500px]">
        <LoadingSpinner size="large" />
        <p className="mt-4 text-gray-500 font-bold animate-pulse">جاري جلب سجل اشتراكاتك...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-xl mx-auto py-16 px-6 text-center">
        <div className="bg-red-50 p-10 rounded-[2.5rem] border border-red-100 shadow-sm">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-black text-gray-900 mb-2">خطأ في الاتصال</h2>
          <p className="text-gray-600 mb-8 leading-relaxed">نعتذر، لم نتمكن من تحميل بيانات الاشتراكات حالياً. يرجى المحاولة مرة أخرى.</p>
          <button
            onClick={() => window.location.reload()}
            className="px-8 py-3 bg-red-600 text-white rounded-xl font-bold hover:bg-red-700 transition-all shadow-lg shadow-red-100"
          >
            إعادة المحاولة
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-10 px-4 sm:px-6 py-8">
      
      {/* Header Section */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
        <div className="space-y-1">
          <h1 className="text-3xl font-black text-gray-900 tracking-tight">إدارة الاشتراكات</h1>
          <p className="text-gray-500 font-medium flex items-center gap-2">
            <ShieldCheck size={18} className="text-primary-600" />
            تابع حالة اشتراكاتك في الكورسات وراجع سجل مدفوعاتك بأمان
          </p>
        </div>
      </div>

      {/* Stats Dashboard */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Active Subs Card */}
        <div className="bg-white p-6 rounded-[2rem] border border-gray-100 shadow-sm flex items-center gap-5 group hover:shadow-md transition-all">
          <div className="p-4 bg-green-50 text-green-600 rounded-2xl group-hover:scale-110 transition-transform">
            <CheckCircle size={28} />
          </div>
          <div>
            <p className="text-xs font-black text-gray-400 uppercase tracking-widest">اشتراكات نشطة</p>
            <p className="text-3xl font-black text-gray-900 leading-none mt-1">
                {subscriptionsData?.subscriptions?.filter(s => s.status === 'active').length || 0}
            </p>
          </div>
        </div>
        
        {/* Expired Subs Card */}
        <div className="bg-white p-6 rounded-[2rem] border border-gray-100 shadow-sm flex items-center gap-5 group hover:shadow-md transition-all">
          <div className="p-4 bg-red-50 text-red-600 rounded-2xl group-hover:scale-110 transition-transform">
            <History size={28} />
          </div>
          <div>
            <p className="text-xs font-black text-gray-400 uppercase tracking-widest">اشتراكات منتهية</p>
            <p className="text-3xl font-black text-gray-900 leading-none mt-1">
                {subscriptionsData?.subscriptions?.filter(s => s.status === 'expired').length || 0}
            </p>
          </div>
        </div>
        
        {/* Total Spent Card */}
        <div className="bg-primary-600 p-6 rounded-[2rem] shadow-xl shadow-primary-100 flex items-center gap-5 group relative overflow-hidden">
          <div className="absolute top-0 left-0 w-20 h-20 bg-white/10 rounded-br-full -z-0"></div>
          <div className="p-4 bg-white/20 text-white rounded-2xl relative z-10 group-hover:scale-110 transition-transform">
            <CreditCard size={28} />
          </div>
          <div className="relative z-10">
            <p className="text-xs font-black text-primary-100 uppercase tracking-widest">إجمالي المدفوع</p>
            <p className="text-2xl font-black text-white leading-none mt-1">
              {subscriptionsData?.subscriptions?.reduce((total, sub) => {
                const price = parseFloat(sub.price) || 0;
                return total + price;
              }, 0).toFixed(2) || '0.00'} 
              <span className="text-sm font-bold mr-1 opacity-80">ج.م</span>
            </p>
          </div>
        </div>
      </div>

      {/* Modern Filter Tabs */}
      <div className="flex flex-wrap items-center gap-3 bg-white p-2 rounded-2xl shadow-sm border border-gray-100 w-fit">
        {[
          { id: 'all', label: 'الكل', count: subscriptionsData?.subscriptions?.length },
          { id: 'active', label: 'نشط', count: subscriptionsData?.subscriptions?.filter(s => s.status === 'active').length },
          { id: 'expired', label: 'منتهي', count: subscriptionsData?.subscriptions?.filter(s => s.status === 'expired').length },
          { id: 'cancelled', label: 'ملغي', count: subscriptionsData?.subscriptions?.filter(s => s.status === 'cancelled').length }
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setFilter(tab.id)}
            className={`px-6 py-2.5 rounded-xl font-black text-sm transition-all flex items-center gap-2 ${
              filter === tab.id
                ? 'bg-primary-600 text-white shadow-lg shadow-primary-100'
                : 'text-gray-500 hover:bg-gray-50'
            }`}
          >
            {tab.label}
            <span className={`text-[10px] px-2 py-0.5 rounded-full ${filter === tab.id ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-500'}`}>
              {tab.count || 0}
            </span>
          </button>
        ))}
      </div>

      {/* Subscriptions List Section */}
      {filteredSubscriptions.length === 0 ? (
        <div className="bg-white rounded-[3rem] p-20 text-center border border-dashed border-gray-200">
          <div className="bg-gray-50 w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-6">
            <CreditCard size={48} className="text-gray-200" />
          </div>
          <h3 className="text-2xl font-black text-gray-900 mb-2">لا توجد سجلات اشتراك</h3>
          <p className="text-gray-500 max-w-sm mx-auto">
            {filter === 'all' 
              ? 'لم تقم بالاشتراك في أي كورس تعليمي بعد. تصفح الكورسات المتاحة الآن وابدأ التعلم.'
              : `لا توجد اشتراكات في قسم "${getStatusText(filter)}" حالياً.`
            }
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-[2.5rem] border border-gray-100 shadow-sm overflow-hidden overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-50">
            <thead className="bg-gray-50/50">
              <tr>
                <th className="px-8 py-5 text-right text-xs font-black text-gray-400 uppercase tracking-widest">محتوى الاشتراك</th>
                <th className="px-8 py-5 text-right text-xs font-black text-gray-400 uppercase tracking-widest">قيمة الدفع</th>
                <th className="px-8 py-5 text-right text-xs font-black text-gray-400 uppercase tracking-widest">الفترة الزمنية</th>
                <th className="px-8 py-5 text-right text-xs font-black text-gray-400 uppercase tracking-widest">الحالة</th>
                <th className="px-8 py-5 text-left text-xs font-black text-gray-400 uppercase tracking-widest">الإجراءات</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-50">
              {filteredSubscriptions.map((subscription) => (
                <tr key={subscription.id} className="hover:bg-primary-50/30 transition-colors group">
                  <td className="px-8 py-6 whitespace-nowrap">
                    <div className="flex items-center gap-4">
                      {subscription.course_image ? (
                        <img
                          src={getImageUrl(subscription.course_image) || '/default_course.png'}
                          alt={subscription.course_title || ''}
                          className="w-12 h-12 rounded-xl object-cover shadow-sm border border-gray-100"
                          onError={(e) => { e.target.onerror = null; e.target.src = '/default_course.png'; }}
                        />
                      ) : (
                        <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center text-gray-400 border border-gray-50 group-hover:bg-white transition-colors">
                          <BookOpen size={20} />
                        </div>
                      )}
                      <div>
                        <div className="text-sm font-black text-gray-900 group-hover:text-primary-600 transition-colors">
                          {subscription.course_title || 'اشتراك مادة دراسية'}
                        </div>
                        <div className="text-[10px] font-bold text-gray-400 uppercase tracking-tighter">
                          معرف الفاتورة: #{subscription.id}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-8 py-6 whitespace-nowrap text-sm">
                    <div className="font-black text-gray-900">{subscription.price || 0} <span className="text-[10px] text-gray-400">ج.م</span></div>
                  </td>
                  <td className="px-8 py-6 whitespace-nowrap">
                    <div className="space-y-1">
                      <div className="flex items-center text-[11px] font-bold text-gray-500 gap-1.5">
                        <Calendar size={12} className="text-green-500" />
                        من: {formatDate(subscription.start_date)}
                      </div>
                      <div className="flex items-center text-[11px] font-bold text-gray-500 gap-1.5">
                        <Calendar size={12} className="text-red-500" />
                        إلى: {formatDate(subscription.end_date)}
                      </div>
                    </div>
                  </td>
                  <td className="px-8 py-6 whitespace-nowrap">
                    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-[10px] font-black border tracking-wider ${getStatusBadge(subscription.status)}`}>
                      {getStatusIcon(subscription.status)}
                      {getStatusText(subscription.status).toUpperCase()}
                    </span>
                  </td>
                  <td className="px-8 py-6 whitespace-nowrap text-left">
                    <div className="flex items-center justify-end gap-3">
                      {subscription.status === 'expired' ? (
                        <button className="flex items-center gap-1.5 text-xs font-black text-white bg-primary-600 hover:bg-primary-700 px-4 py-2 rounded-xl transition-all shadow-md shadow-primary-100">
                          إعادة تفعيل
                        </button>
                      ) : null}
                      
                      <button onClick={() => navigate(`/courses/${subscription.course_id || subscription.courseId || subscription.courseId}`)} className="p-2 text-gray-400 hover:text-gray-900 transition-colors rounded-lg hover:bg-gray-100" title="عرض تفاصيل الكورس">
                        <ExternalLink size={18} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Helper Support Card */}
      <div className="bg-gray-900 rounded-[2.5rem] p-10 text-white flex flex-col md:flex-row items-center justify-between gap-8 relative overflow-hidden group">
        <div className="absolute top-0 right-0 w-64 h-64 bg-primary-600/20 rounded-full blur-3xl -z-0"></div>
        <div className="relative z-10 text-center md:text-right">
            <h3 className="text-2xl font-black mb-2 flex items-center gap-2 justify-center md:justify-start">
                <RefreshCw /> هل تود تغيير طريقة الدفع؟
            </h3>
            <p className="text-gray-400 font-medium opacity-90">يمكنك تحديث بياناتك المالية أو اختيار وسيلة دفع جديدة من خلال إعدادات الحساب.</p>
        </div>
        <button onClick={() => navigate('/settings')} className="relative z-10 bg-white text-gray-900 px-10 py-4 rounded-2xl font-black flex items-center gap-2 shadow-xl hover:bg-primary-50 transition-colors">
          تحديث البيانات <ChevronLeft size={20} />
        </button>
      </div>
    </div>
  );
};

// أيقونات إضافية مستخدمة في التصميم
const BookOpen = ({ size, className }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className={className}>
        <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
        <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
    </svg>
);

export default StudentSubscriptions;